import React from 'react';
import styled from 'styled-components';
import colors from '../../constants/colors';
import { ReactSVG } from 'react-svg';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../constants/initialStore';
import { otherFormats } from '../../constants/otherFormats';
import {
  ConvertMMToPixel,
} from '../../utils/convertCMToPixel';
import { PhotoQualityLevel } from '../../utils/calculatePhotoQuality';

interface PhotoQualityIndicatorProps {
  qualityLevel: PhotoQualityLevel;
  dpi: number;
  elementWidth: number;
  elementHeight: number;
  elementTop: number;
  elementLeft: number;
  forceBottomLeft?: boolean;
}

const PhotoQualityIndicator: React.FC<PhotoQualityIndicatorProps> = ({
  qualityLevel,
  dpi,
  elementWidth,
  elementHeight,
  elementTop,
  elementLeft,
  forceBottomLeft = false,
}) => {
  const { t } = useTranslation(['common']);
  const zoom = useSelector((state: RootState) => state.zoom.value);
  const ratio = useSelector((state: RootState) => state.ratio.value);
  const format = useSelector((state: RootState) =>
    state.creation.present.other
      ? state.creation.present.other.format
      : undefined,
  );

  const isElementTooSmall = !forceBottomLeft &&
    (elementWidth * ratio * zoom < 60 || elementHeight * ratio * zoom < 40);

  if (isElementTooSmall) {
    return null;
  }

  // In preview mode, compute badge size so it appears at a consistent visual size
  // regardless of zoom. Divide target visual px by zoom to get canvas px.
  // Clamp between a min and max visual size, proportional to element's natural height.
  let badgeSize = 24;
  let textSize = 12;
  let indicatorPadding = '4px 8px';
  let indicatorMinWidth = 32;
  let borderWidth = 2;
  let borderRadius = 16;

  if (forceBottomLeft) {
    // Use large fixed canvas values — the iframe renders the page scaled down,
    // so large values here appear at the right visual size on screen.
    badgeSize = 80;
    textSize = 40;
    indicatorPadding = '12px 24px';
    indicatorMinWidth = 128;
    borderWidth = 6;
    borderRadius = 50;
  }

  const getQualityInfo = () => {
    switch (qualityLevel) {
      case PhotoQualityLevel.EXCELLENT:
        return { icon: '/svg/badge_happy.svg', text: t('photos.qualityExcellent', { dpi }) };
      case PhotoQualityLevel.GOOD:
        return { icon: '/svg/badge_happy.svg', text: t('photos.qualityGood', { dpi }) };
      case PhotoQualityLevel.POOR:
        return { icon: '/svg/badge_sad.svg', text: t('photos.qualityPoor', { dpi }) };
      default:
        return { icon: '/svg/badge_sad.svg', text: t('photos.qualityUnknown') };
    }
  };

  const qualityInfo = getQualityInfo();

  return (
    <Indicator
      $qualityLevel={qualityLevel}
      $zoom={zoom}
      $ratio={ratio}
      $elementWidth={elementWidth}
      $elementHeight={elementHeight}
      $forceBottomLeft={forceBottomLeft}
      $badgeSize={badgeSize}
      $indicatorPadding={indicatorPadding}
      $indicatorMinWidth={indicatorMinWidth}
      $borderWidth={borderWidth}
      $borderRadius={borderRadius}
      $isBottomToile={
        format !== undefined &&
        format.includes('TOI') &&
        elementHeight + elementTop >
          ConvertMMToPixel(otherFormats[format].effectiveHeight ?? 999999)
      }
      $isLeftToile={
        format !== undefined &&
        format.includes('TOI') &&
        elementLeft <
          ConvertMMToPixel(
            (otherFormats[format].width -
              (otherFormats[format].effectiveWidth ?? 0)) / 2
          )
      }
    >
      <Badge $badgeSize={badgeSize}>
        <ReactSVG src={qualityInfo.icon} />
      </Badge>
      <Text $forceBottomLeft={forceBottomLeft} $textSize={textSize} $badgeSize={badgeSize}>
        {qualityInfo.text}
      </Text>
    </Indicator>
  );
};

const Badge = styled.div<{ $badgeSize: number }>`
  width: ${p => p.$badgeSize}px !important;
  height: ${p => p.$badgeSize}px;
  flex-shrink: 0;

  svg {
    width: ${p => p.$badgeSize}px;
    height: ${p => p.$badgeSize}px;
  }
`;

const Text = styled.div<{ $forceBottomLeft: boolean; $textSize: number; $badgeSize: number }>`
  max-width: ${p => p.$forceBottomLeft ? '500px' : '0'};
  overflow: hidden;
  transition: ${p => p.$forceBottomLeft ? 'none' : 'max-width 0.3s ease-out'};
  white-space: ${p => p.$forceBottomLeft ? 'normal' : 'nowrap'};
  line-height: 1.3;
  font-size: ${p => p.$textSize}px;
  font-weight: 500;
  margin-left: ${p => p.$forceBottomLeft ? p.$badgeSize * 0.15 : 4}px;
`;

const Indicator = styled.div<{
  $qualityLevel: PhotoQualityLevel;
  $zoom: number;
  $ratio: number;
  $elementWidth: number;
  $elementHeight: number;
  $forceBottomLeft: boolean;
  $badgeSize: number;
  $indicatorPadding: string;
  $indicatorMinWidth: number;
  $borderWidth: number;
  $borderRadius: number;
  $isBottomToile?: boolean;
  $isLeftToile?: boolean;
}>`
  display: flex !important;
  justify-content: center;
  align-items: center;
  width: fit-content;
  min-width: ${p => p.$indicatorMinWidth}px;
  height: ${p => p.$forceBottomLeft ? 'auto' : '32px'};
  position: absolute;
  transition: height 0.3s ease-out;

  ${({ $isBottomToile, $elementHeight, $ratio, $zoom, $forceBottomLeft }) => {
    if ($forceBottomLeft) {
      return `bottom: 6px; transform-origin: bottom left;`;
    }
    const elementDisplayHeight = $elementHeight * $ratio * $zoom;
    if ($isBottomToile || elementDisplayHeight < 60) {
      return `top: -32px; transform-origin: bottom left;`;
    }
    return `bottom: 6px; transform-origin: top left;`;
  }}

  left: 6px;
  transform: ${({ $zoom, $forceBottomLeft }) => $forceBottomLeft ? 'none' : `scale(${Math.min(1 / $zoom, 1)})`};
  border-radius: ${p => p.$borderRadius}px;
  padding: ${p => p.$indicatorPadding};
  max-width: none;

  background-color: ${({ $qualityLevel }) => {
    switch ($qualityLevel) {
      case PhotoQualityLevel.EXCELLENT: return colors.success50;
      case PhotoQualityLevel.GOOD: return '#FEF3C7';
      case PhotoQualityLevel.POOR: return colors.error50;
      default: return colors.error50;
    }
  }};

  border: ${p => p.$borderWidth}px solid ${({ $qualityLevel }) => {
    switch ($qualityLevel) {
      case PhotoQualityLevel.EXCELLENT: return colors.success200;
      case PhotoQualityLevel.GOOD: return '#F59E0B';
      case PhotoQualityLevel.POOR: return colors.error200;
      default: return colors.error200;
    }
  }};

  color: ${({ $qualityLevel }) => {
    switch ($qualityLevel) {
      case PhotoQualityLevel.EXCELLENT: return colors.success700;
      case PhotoQualityLevel.GOOD: return '#92400E';
      case PhotoQualityLevel.POOR: return colors.error700;
      default: return colors.error700;
    }
  }};

  box-sizing: border-box;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 200;
  cursor: pointer;
  pointer-events: auto;

  ${({ $forceBottomLeft, $zoom, $ratio, $elementWidth, $elementHeight }) => {
    if ($forceBottomLeft) return '';

    const rules: string[] = [];

    if (!$forceBottomLeft) {
      rules.push(`
        &:hover {
          height: auto;
          min-height: 40px;
        }
      `);
    }

    if ($zoom < 0.5) {
      rules.push(`font-size: 10px; height: 28px;`);
    }

    const displayWidth = $elementWidth * $ratio * $zoom;
    const displayHeight = $elementHeight * $ratio * $zoom;
    if (displayWidth < 100 || displayHeight < 50) {
      rules.push(`font-size: 10px; height: 24px; padding: 2px 4px;`);
    }

    return rules.join('\n');
  }}
`;

export default PhotoQualityIndicator;
